import { apiClient } from './client'

export type V2Lifecycle = 'candidate' | 'active' | 'superseded' | 'archived' | 'purged'

export interface V2MemorySummary {
  memoryId: string
  memoryClass: 'world' | 'experience' | 'reflection'
  kind?: 'decision' | 'lesson' | 'fact' | 'preference' | 'constraint' | 'open_issue'
  lifecycle: V2Lifecycle
  versionNumber: number
  importance?: number
  confidence?: number
  excerpt?: string
  provenanceIds: string[]
  updatedAt: string
  /** Creator username when known; omitted when missing. */
  createdByUsername?: string
}

export interface AgentSession {
  sessionId: string
  status: 'active' | 'completed' | 'abandoned'
  agentId?: string
  sourceClient: string
  externalAliases: Array<{ sourceClient: string; externalSessionId: string }>
  parentSessionId?: string
  extractionStatus: string
  handoffStatus: string
  startedAt: string
  lastEventAt: string
  endedAt?: string
}

export interface SessionHandoffContent {
  outcome: string
  status: string
  changedResources: string[]
  decisions: string[]
  lessons: string[]
  tests: string[]
  issues: string[]
  nextActions: string[]
}

export interface SessionHandoff {
  handoffId: string
  sessionId: string
  versionNumber: number
  content: SessionHandoffContent
  references: Record<string, string[]>
  verifiedResourceIds: string[]
  agentAssertions: SessionHandoffContent
  observedEvidence: Record<string, unknown>
  createdAt: string
}

export type MemorySummary = V2MemorySummary

export interface VerifyMemoryResult {
  schemaVersion: '2.0'
  memoryId: string
  verificationState: string
  sourceChain: string[]
  sourceAvailable: boolean
  conflictIds: string[]
  supersessionChain: string[]
  explanation: string
}

export interface MemoryTimelineItem {
  kind: 'observation' | 'memory_version' | 'relationship' | 'session' | 'handoff'
  id: string
  occurredAt: string
  summary: string
}

export interface V2MemoryRecord {
  memoryId: string
  orgId: number
  projectId: number
  memoryClass: V2MemorySummary['memoryClass']
  kind?: V2MemorySummary['kind']
  lifecycle: V2Lifecycle
  importance: number
  confidence: number
  currentVersion: number
  createdAt: string
  updatedAt: string
}

export interface PendingJob {
  jobId: string
  status: string
  reasonCode?: string
}

function params(query: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, value)
  }
  return search.toString()
}

export const memoryV2Api = {
  async searchV2Memories(
    projectName: string,
    query: string,
    filters?: Record<string, unknown>,
    limit?: number,
    cursor?: string
  ): Promise<{
    memories: V2MemorySummary[]
    nextCursor?: string
    degraded: boolean
    warnings: string[]
  }> {
    return apiClient.post('/memory-v2/search', {
      projectName,
      query,
      ...(filters !== undefined ? { filters } : {}),
      ...(limit !== undefined ? { limit } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    })
  },

  async getV2Timeline(
    projectName: string,
    selector: { memoryId?: string; sessionId?: string }
  ): Promise<{ items: MemoryTimelineItem[] }> {
    return apiClient.get(`/memory-v2/timeline?${params({ projectName, ...selector })}`)
  },

  async getV2Verify(projectName: string, memoryId: string): Promise<VerifyMemoryResult> {
    return apiClient.get(`/memory-v2/verify?${params({ projectName, memoryId })}`)
  },

  async listV2Sessions(
    projectName: string,
    status?: AgentSession['status']
  ): Promise<{ sessions: AgentSession[] }> {
    return apiClient.get(`/sessions?${params({ projectName, status })}`)
  },

  async getV2Handoffs(
    projectName: string,
    sessionId: string
  ): Promise<{ session: AgentSession; handoffs: SessionHandoff[] }> {
    return apiClient.get(`/sessions/${sessionId}?${params({ projectName })}`)
  },

  async archiveV2Memory(projectName: string, memoryId: string): Promise<V2MemoryRecord> {
    return apiClient.post<V2MemoryRecord>('/memory-v2/archive', { projectName, memoryId })
  },

  async restoreV2Memory(projectName: string, memoryId: string): Promise<V2MemoryRecord> {
    return apiClient.post<V2MemoryRecord>('/memory-v2/restore', { projectName, memoryId })
  },

  async getV2ProjectContext(projectName: string): Promise<{ memories: V2MemorySummary[] }> {
    return apiClient.get(`/memory-v2/project-context?${params({ projectName })}`)
  },

  async getV2JobStatus(
    projectName: string,
    selector: { jobId?: string; observationId?: string }
  ): Promise<PendingJob> {
    return apiClient.get(`/memory-v2/job-status?${params({ projectName, ...selector })}`)
  },
}
